import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {
  BfmMonthlyComponent,
  RESULTS_ACCORDION_CONFIG,
  RESULTS_COA_VALUE_CONFIG,
  RESULTS_MONTHLY_CONFIG,
  RESULTS_PERIOD_FILTER_CONFIG,
} from "@celerofinancas/ui-reports";
import { MonthlyReportWarningComponent } from "@pages/results/monthly-report-warning/monthly-report-warning.component";
import { EXPENSE_COLORS, REVENUES_COLORS } from "@pages/results/monthly-report/consts/chart-colors";
import {
  resultsColorDict,
  resultsNameList
} from "@pages/results/monthly-report/consts/results-name-list";
import { resultsTooltip } from "@pages/results/monthly-report/consts/results-tooltips";
import { triggerReportsBootstrapWarmup } from "@pages/results/shared/reports-bootstrap-warmup";
import { MainTitleComponent } from "@shared/components/main-title/main-title.component";
import { NavigationBarComponent } from "@shared/components/navigation-bar/navigation-bar.component";
import { BootstrapCoordinator } from '@shared/models/bootstrap-coordinator';

/**
 * Monthly Reports Component
 */
@Component({
  selector: 'caixa-webcomponent-monthly-reports',
  templateUrl: './monthly-reports.component.html',
  styleUrls: ['./monthly-reports.component.scss'],
  standalone: true,
  imports: [
    BfmMonthlyComponent,
    MainTitleComponent,
    NavigationBarComponent,
    MonthlyReportWarningComponent,
  ],
  providers: [
    {
      provide: RESULTS_MONTHLY_CONFIG,
      useValue: {
        tabsWrapperPosition: 'top',
        monthlyAnalysisLabel: 'Análise mensal',
        resultsTabLabel: 'DFM por Resultados',
        chartAccountsTabLabel: 'DFM por Categorias',
        customIndicatorsTabLabel: 'Indicadores personalizados',
        useQueryParams: false,
        transitoryAccountsTooltip:
          'Movimentações transitórias são: transferência entre contas; transações internas; resgates; aplicações automáticas e estornos de pagamentos ou recebimentos.',
      },
    },
    {
      provide: RESULTS_ACCORDION_CONFIG,
      useValue: {
        showAccordionItemFirstLevel: false,
        showAccordionHeader: true,
        positionResultsTooltipDictionary: 'content',
        resultsTooltipDict: resultsTooltip,
        resultsNameList: resultsNameList,
        resultsColorDict: resultsColorDict,
        allowExpandBalanceSection: false,
        balanceStartDescription: 'Saldo inicial agrupado',
        balanceEndDescription: 'Saldo final agrupado',
      },
    },
    {
      provide: RESULTS_PERIOD_FILTER_CONFIG,
      useValue: {
        selectPeriodLabel: 'Período de análise',
        showCostCenter: true,
        showCostCenterTooltip: true,
        buttonFilterText: 'Analisar',
        useQueryParams: false,
      },
    },
    {
      provide: RESULTS_COA_VALUE_CONFIG,
      useValue: {
        chartType: 'doughnut',
        chartTypeCutout: '75%',
        chartTypeAspectRatio: 3,
        expenseColors: EXPENSE_COLORS,
        revenueColors: REVENUES_COLORS,
        showTooltip: true,
        financialRecordColumnList: [
          { name: 'status', size: 1, align: 'center' },
          { name: 'due_at', size: 1, align: 'left' },
          { name: 'name', size: 4, align: 'left' },
          { name: 'coa_name', size: 2, align: 'left' },
          { name: 'bank_id', size: 2, align: 'center' },
          { name: 'value', size: 1, align: 'left' },
        ],
      }
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MonthlyReportsComponent extends BootstrapCoordinator implements OnInit, AfterViewInit {
  /**
   * Template for the magnifier icon
   */
  @ViewChild('magnifierTemplate', { read: TemplateRef, static: false })
  public magnifierTemplateRef!: TemplateRef<unknown>;

  /**
   * COA Value Config
   */
  private readonly coaConfig = inject(RESULTS_COA_VALUE_CONFIG);

  /**
   * Headers for the cash flow table
   */
  public headersFinancialRecords = {
    status: 'Status',
    due_at: 'Vencimento',
    name: 'Nome',
    coa_name: 'Categoria',
    bank_id: 'Banco e Conta',
    value: 'Valor (R$)',
    magnifier: '',
  };

  /**
   * Indicates whether to show bank details in financial records
   */
  public bankDetailsFinancialRecords = true;

  /**
   * Lifecycle hook that is called once the component is initialized. Triggers
   * the core-registry bootstrap because this page does not fire any HTTP
   * request on mount, which would otherwise leave the cost center filter
   * empty until the user clicks "Analisar".
   */
  public override ngOnInit(): void {
    super.ngOnInit();
    triggerReportsBootstrapWarmup(this.store);
  }

  /**
   * Lifecycle hook that is called after the component's view has been initialized.
   */
  public ngAfterViewInit(): void {
    // Add the custom column with template after the view is initialized
    this.coaConfig.financialRecordColumnList.push({
      name: 'custom',
      size: 1,
      align: 'center',
      template: () => this.magnifierTemplateRef
    });
  }
}
